'use client';
import { resolveImageAlt } from '@eliadmin/core/src/utils/image-alt-utils';

import type { FetchBlogsQuery } from '@eliadmin/core/src/api/blogs/blogs-api';
import { fetchBlogs } from '@eliadmin/core/src/api/blogs/blogs-api';
import type { BlogCategory } from '@eliadmin/core/src/api/blogs/blogs-types';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { generateBlogKey } from '@eliadmin/core/src/utils/blog-utils';
import { useSuspenseInfiniteQuery } from '@tanstack/react-query';
import Image from '@eliadmin/core/src/components/image/image-wrapped';
import Link from '@eliadmin/core/src/components/link';
import { useSearchParams } from 'next/navigation';
import { useMemo } from 'react';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { createContextStore } from '@eliadmin/core/src/stores/context-store';
import { Badge } from '@eliadmin/core/src/components/ui/badge';
import { Card, CardContent } from '@eliadmin/core/src/components/ui/card';
import { BLOGS_CONFIG } from '@eliadmin/core/src/constants/blogs-config';
import React from 'react';
import { getTextFromHTML } from '@eliadmin/core/src/utils/misc-utils';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { HtmlRendererRaw } from '@eliadmin/core/src/components/ui/html-renderer-raw';

const [BlogCategoriesProvider, useBlogCategories] = createContextStore<
  { categories: BlogCategory[]; activeCategoryIds: string[] },
  { setActiveCategories: (categoryIds: string[]) => void }
>(
  {
    categories: [],
    activeCategoryIds: [],
  },
  set => ({
    setActiveCategories: (categoryIds: string[]) => set({ activeCategoryIds: categoryIds }),
  }),
);

export { BlogCategoriesProvider, useBlogCategories };

export type BlogCardsProps = {
  locale: string;
  query: FetchBlogsQuery;
  className?: {
    blog?: {
      main?: string;
      link?: string;
      image?: string;
      title?: string;
      date?: string;
      contentContainer?: string;
      content?: string;
    };
  };
};

export const BlogCards = ({ locale, query: queryProp, className }: BlogCardsProps) => {
  const { t } = useTranslations();

  const searchParams = useSearchParams();

  const search = searchParams.get('search');
  const activeCategories = useBlogCategories(s => s.activeCategoryIds);

  const query = useMemo(
    () => ({
      where:
        !('filter' in queryProp) && activeCategories.length > 0
          ? { categories: { in: activeCategories } }
          : undefined,
      search: search ?? undefined,
      ...queryProp,
    }),
    [activeCategories, queryProp, search],
  );

  const fetchBlogsFn = async ({ pageParam = 1 }) => {
    const { data } = await fetchBlogs({
      locale,
      query: { ...query, page: pageParam } as any,
    });
    return data;
  };

  const {
    data: blogs,
    fetchNextPage,
    isFetching,
  } = useSuspenseInfiniteQuery({
    initialPageParam: 0,
    queryKey: ['blogs', query],
    queryFn: fetchBlogsFn,
    getNextPageParam: (lastPage, pages) => pages.length + 1,
  });

  return (
    <div className="flex w-full flex-col">
      <div
        className={cn(
          'flex flex-wrap justify-center gap-4 md:justify-start',
          isFetching && 'opacity-30',
        )}
      >
        {blogs?.pages.map((p, pageIndex) => {
          return p.map((blog, i) => {
            if (i === 0) return;
            return (
              <Card
                key={generateBlogKey(blog)}
                className={cn(
                  'relative w-[400px] max-w-[90vw]',
                  'ea-blog-card',
                  className?.blog?.main,
                )}
              >
                <Link
                  href={ROUTES.BLOG_DETAIL({ slug: blog.slug }) ?? '#'}
                  className={cn('ea-blog-card__link', className?.blog?.link)}
                  prefetch={false}
                >
                  <CardContent
                    className={cn(
                      'p-0',
                      'ea-blog-card__content',
                      className?.blog?.contentContainer,
                    )}
                  >
                    <Image
                      alt={resolveImageAlt(blog?.image, blog?.name, 'image')}
                      src={blog.image?.url || '/images/placeholder.png'}
                      width={400}
                      height={225}
                      sizes="(max-width: 444px) 90vw, 400px"
                      loading={pageIndex === 0 && i <= 2 ? 'eager' : 'lazy'}
                      className={cn(
                        'h-[225px] w-full ',
                        'ea-blog-card__image',
                        className?.blog?.image,
                      )}
                    />

                    <div
                      className={cn(
                        'flex flex-col space-y-4 p-4 pt-0',
                        'ea-blog-card__content-container',
                        className?.blog?.contentContainer,
                      )}
                    >
                      <Badge
                        size={'md'}
                        className={cn(
                          'pointer-events-none flex flex-grow-0 items-center gap-1 rounded-md bg-gray-100 px-2 text-xs font-semibold leading-5 text-foreground opacity-80',
                          'ea-blog-card__date-badge',
                          'absolute left-4 top-4',
                          // className.blog?
                        )}
                      >
                        {React.cloneElement(BLOGS_CONFIG.blogCard.calendarIcon as any, {
                          className: cn('ea-availability-label__icon'),
                        })}
                        {new Date(blog.created_at).toLocaleDateString(locale)}
                      </Badge>

                      <h2
                        data-ea-visual-tag="h6"
                        className={cn(
                          'line-clamp-2 h-8 text-ellipsis text-base font-bold leading-4',
                          'ea-blog-card__title',
                          className?.blog?.title,
                        )}
                      >
                        {blog.name}
                      </h2>
                      <HtmlRendererRaw
                        as="p"
                        className={cn(
                          'line-clamp-3 h-12 text-ellipsis text-sm leading-4 text-muted-foreground',
                          'ea-blog-card__content',
                          className?.blog?.content,
                        )}
                        html={getTextFromHTML(blog.content).replace(/&nbsp;/gm, '')}
                      />
                    </div>
                  </CardContent>
                </Link>
              </Card>
            );
          });
        })}
      </div>
      {blogs?.pages.length === 1 && blogs?.pages?.every(page => page.length === 0) && (
        <div className="flex w-full items-center justify-center">
          <p className="text-foreground">{t('text554')}</p>
        </div>
      )}
      <div className="flex justify-center">
        {blogs?.pages.length > 0 && blogs?.pages[blogs?.pages.length - 1].length > 0 && (
          <Button
            onClick={() => fetchNextPage()}
            variant={'outline'}
            disabled={isFetching}
            className=""
          >
            {t('text588')}
          </Button>
        )}
      </div>
    </div>
  );
};
